import React, { useEffect, useRef, useState } from "react";
import logoSrc from "../../assets/logo-mark-2026.png";

// RDS-SPLASH-v2: mismo patrón aprobado e implementado en Citas by Shessai
// (frames exactos: fade-in 250ms -> pulso sutil mientras carga -> fade-out
// adaptativo, tope duro de 1.5s). Reemplaza la v1 (anillo girando + punto
// orbitando + shimmer + logo SVG dibujado a mano), que usaba el fondo oscuro
// de Agenda -- acá el fondo es blanco (#FFFFFF) a propósito, para
// diferenciar el CRM de Agenda a simple vista.
//
// `done` -- true cuando el contenido real ya está listo detrás de este
// overlay. El splash NUNCA espera el tope completo si `done` llega antes:
// solo respeta un mínimo visible (para que el logo no sea un solo frame
// parpadeando) y luego sale en cuanto puede. El tope de 1.5s es un techo de
// seguridad, no una duración fija -- a diferencia de Agenda (carga
// síncrona), el CRM sí espera una llamada real a /auth/refresh_token, así
// que es más probable que este techo se alcance en la práctica.
const MIN_VISIBLE_MS = 450; // fade-in (250ms) + margen mínimo de lectura
const MAX_VISIBLE_MS = 1500; // techo duro, nunca más que esto
const FADE_OUT_MS = 300;

const LoadingSplash = ({ done = false }) => {
  const [phase, setPhase] = useState("enter"); // enter -> hold -> exit -> gone
  const mountedAt = useRef(
    typeof performance !== "undefined" ? performance.now() : Date.now()
  );

  useEffect(() => {
    const t = setTimeout(() => setPhase((p) => (p === "enter" ? "hold" : p)), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => setPhase((p) => (p === "gone" || p === "exit" ? p : "exit")),
      MAX_VISIBLE_MS
    );
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!done || phase !== "hold") return;
    const elapsed = (typeof performance !== "undefined" ? performance.now() : Date.now()) - mountedAt.current;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const t = setTimeout(() => setPhase((p) => (p === "hold" ? "exit" : p)), remaining);
    return () => clearTimeout(t);
  }, [done, phase]);

  useEffect(() => {
    if (phase !== "exit") return;
    const t = setTimeout(() => setPhase("gone"), FADE_OUT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === "gone") return null;

  return (
    <div className={`shessai-splash-root shessai-splash-${phase}`}>
      <style>{`
        .shessai-splash-root {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #FFFFFF;
          opacity: 0;
        }
        .shessai-splash-enter, .shessai-splash-hold {
          animation: shessai-splash-in 250ms ease-out forwards;
        }
        .shessai-splash-exit {
          animation: shessai-splash-out ${FADE_OUT_MS}ms ease-in-out forwards;
        }
        @keyframes shessai-splash-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes shessai-splash-out {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        .shessai-splash-logo {
          width: 96px;
          height: 96px;
          opacity: 0;
          transform: scale(0.94);
          animation: shessai-logo-in 250ms ease-out forwards;
        }
        .shessai-splash-hold .shessai-splash-logo {
          animation:
            shessai-logo-in 250ms ease-out forwards,
            shessai-logo-pulse 1.2s ease-in-out 250ms infinite;
        }
        @keyframes shessai-logo-in {
          from { opacity: 0; transform: scale(0.94); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes shessai-logo-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.035); }
        }
        @media (prefers-reduced-motion: reduce) {
          .shessai-splash-root, .shessai-splash-logo { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
      <img className="shessai-splash-logo" src={logoSrc} alt="Shessai" />
    </div>
  );
};

export default LoadingSplash;
